import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Sparkles,
  X,
  Loader2,
  ChevronRight,
  ChevronLeft,
  FileText,
  Settings,
  Eye,
  Check,
  Lightbulb,
  Target,
  Users,
  Wand2,
  AlertTriangle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { articlesApi, sitesApi, healthApi } from "@/services/api";
import type { ArticleGenerateRequest, Site, WordPressCategory } from "@/types";
import { cn } from "@/lib/utils";

interface ArticleGenerationWizardProps {
  onSuccess?: (articleId: string) => void;
  onCancel?: () => void;
}

type WizardStep = "topic" | "style" | "advanced" | "preview";

const steps: { id: WizardStep; labelKey: string; icon: typeof FileText }[] = [
  {
    id: "topic",
    labelKey: "articleGeneration.steps.topicKeywords",
    icon: Lightbulb,
  },
  {
    id: "style",
    labelKey: "articleGeneration.steps.styleSettings",
    icon: Settings,
  },
  {
    id: "advanced",
    labelKey: "articleGeneration.steps.advancedOptions",
    icon: Wand2,
  },
  {
    id: "preview",
    labelKey: "articleGeneration.steps.previewGenerate",
    icon: Eye,
  },
];

const toneKeys: Record<string, { labelKey: string; descKey: string }> = {
  professional: {
    labelKey: "articleGeneration.style.tone.professional",
    descKey: "articleGeneration.style.tone.professionalDesc",
  },
  casual: {
    labelKey: "articleGeneration.style.tone.casual",
    descKey: "articleGeneration.style.tone.casualDesc",
  },
  formal: {
    labelKey: "articleGeneration.style.tone.formal",
    descKey: "articleGeneration.style.tone.formalDesc",
  },
  friendly: {
    labelKey: "articleGeneration.style.tone.friendly",
    descKey: "articleGeneration.style.tone.friendlyDesc",
  },
};

type AudienceType = "general" | "technical" | "beginners" | "business";

const audienceOptions: {
  value: AudienceType;
  labelKey: string;
  descKey: string;
}[] = [
  {
    value: "general",
    labelKey: "articleGeneration.style.audience.general",
    descKey: "articleGeneration.style.audience.generalDesc",
  },
  {
    value: "technical",
    labelKey: "articleGeneration.style.audience.technical",
    descKey: "articleGeneration.style.audience.technicalDesc",
  },
  {
    value: "beginners",
    labelKey: "articleGeneration.style.audience.beginners",
    descKey: "articleGeneration.style.audience.beginnersDesc",
  },
  {
    value: "business",
    labelKey: "articleGeneration.style.audience.business",
    descKey: "articleGeneration.style.audience.businessDesc",
  },
];

export function ArticleGenerationWizard({
  onSuccess,
  onCancel,
}: ArticleGenerationWizardProps) {
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState<WizardStep>("topic");

  // Default article language to Hebrew if UI is in Hebrew
  const defaultLanguage = i18n.language?.startsWith("he") ? "he" : "en";

  // Check for ?duplicate= query param to pre-fill from a cloned article.
  const duplicateConfig = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get("duplicate");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const [formData, setFormData] = useState<ArticleGenerateRequest>({
    site_id: duplicateConfig?.site_id || "",
    topic: duplicateConfig?.topic || "",
    keywords: duplicateConfig?.keywords || [],
    target_word_count: duplicateConfig?.target_word_count || 800,
    tone: duplicateConfig?.tone || "professional",
    language: duplicateConfig?.language || defaultLanguage,
    include_image: duplicateConfig?.include_image ?? true,
    image_instructions: duplicateConfig?.image_instructions || "",
    audience: duplicateConfig?.audience || "general",
    additional_instructions: duplicateConfig?.additional_instructions || "",
    pipeline_version: "v3",
    include_h1: duplicateConfig?.include_h1 ?? false,
    auto_publish: duplicateConfig?.auto_publish ?? false,
    reference_urls: duplicateConfig?.reference_urls || ([] as string[]),
  });
  const [keywordInput, setKeywordInput] = useState("");
  const [suggestedKeywords] = useState<string[]>([
    "SEO",
    "optimization",
    "best practices",
    "guide",
    "tips",
    "strategies",
  ]);

  // Fetch available sites
  const { data: sitesData, isLoading: sitesLoading } = useQuery({
    queryKey: ["sites"],
    queryFn: () => sitesApi.list({ is_active: true }),
  });

  // Fetch categories for selected site
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ["site-categories", formData.site_id],
    queryFn: () => sitesApi.getCategories(formData.site_id),
    enabled: !!formData.site_id,
  });

  const categories: WordPressCategory[] = categoriesData?.categories || [];

  // Check if system is ready for generation (Celery workers available)
  const { data: healthData, isLoading: healthLoading } = useQuery({
    queryKey: ["health-ready"],
    queryFn: () => healthApi.checkReady(),
    refetchInterval: 30000, // Check every 30 seconds
    retry: false,
  });

  const celeryAvailable = healthData?.checks?.celery?.status === "ok";

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: articlesApi.generate,
    onSuccess: (data) => {
      onSuccess?.(data.id);
    },
  });

  const sites = sitesData?.items || [];
  const selectedSite = sites.find((s: Site) => s.id === formData.site_id);

  const handleAddKeyword = (keyword?: string) => {
    const kw = (keyword || keywordInput).trim().toLowerCase();
    if (kw && !formData.keywords?.includes(kw)) {
      setFormData((prev) => ({
        ...prev,
        keywords: [...(prev.keywords || []), kw],
      }));
      if (!keyword) setKeywordInput("");
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setFormData((prev) => ({
      ...prev,
      keywords: prev.keywords?.filter((k) => k !== keyword) || [],
    }));
  };

  const handleSubmit = () => {
    if (!formData.site_id || !formData.topic) return;
    // Clean reference_urls: trim + drop empties (inputs may have blank slots)
    const urls = (formData.reference_urls || [])
      .map((u) => u.trim())
      .filter(Boolean)
      .slice(0, 5);
    generateMutation.mutate({ ...formData, reference_urls: urls });
  };

  const canProceed = (): boolean => {
    switch (currentStep) {
      case "topic":
        return !!formData.site_id && !!formData.topic.trim();
      case "style":
        return true;
      case "advanced":
        return true;
      case "preview":
        return true;
      default:
        return false;
    }
  };

  const goToStep = (step: WizardStep) => {
    const currentIndex = steps.findIndex((s) => s.id === currentStep);
    const targetIndex = steps.findIndex((s) => s.id === step);
    if (targetIndex <= currentIndex || canProceed()) {
      setCurrentStep(step);
    }
  };

  const nextStep = () => {
    const currentIndex = steps.findIndex((s) => s.id === currentStep);
    const nextStepItem = steps[currentIndex + 1];
    if (currentIndex < steps.length - 1 && canProceed() && nextStepItem) {
      setCurrentStep(nextStepItem.id);
    }
  };

  const prevStep = () => {
    const currentIndex = steps.findIndex((s) => s.id === currentStep);
    const prevStepItem = steps[currentIndex - 1];
    if (currentIndex > 0 && prevStepItem) {
      setCurrentStep(prevStepItem.id);
    }
  };

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);

  return (
    <Card className="w-full max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          {t("articleGeneration.title")}
        </CardTitle>
        <CardDescription>{t("articleGeneration.description")}</CardDescription>
      </CardHeader>

      {/* Step Indicator */}
      <div className="px-6 pb-4">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = index < currentStepIndex;
            const isClickable = index <= currentStepIndex || canProceed();

            return (
              <div key={step.id} className="flex items-center">
                <button
                  onClick={() => isClickable && goToStep(step.id)}
                  disabled={!isClickable}
                  className={cn(
                    "flex flex-col items-center gap-1 transition-colors",
                    isClickable
                      ? "cursor-pointer"
                      : "cursor-not-allowed opacity-50",
                  )}
                >
                  <div
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors",
                      isActive &&
                        "border-primary bg-primary text-primary-foreground",
                      isCompleted &&
                        "border-primary bg-primary/10 text-primary",
                      !isActive &&
                        !isCompleted &&
                        "border-muted-foreground/30 text-muted-foreground",
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <StepIcon className="h-5 w-5" />
                    )}
                  </div>
                  <span
                    className={cn(
                      "text-xs font-medium",
                      isActive && "text-primary",
                      !isActive && "text-muted-foreground",
                    )}
                  >
                    {t(step.labelKey)}
                  </span>
                </button>
                {index < steps.length - 1 && (
                  <div
                    className={cn(
                      "w-12 h-0.5 mx-2",
                      index < currentStepIndex
                        ? "bg-primary"
                        : "bg-muted-foreground/30",
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <CardContent>
        {/* Step 1: Topic & Keywords */}
        {currentStep === "topic" && (
          <div className="space-y-6">
            {/* Site Selection */}
            <div className="space-y-2">
              <Label htmlFor="site">
                {t("articleGeneration.topic.targetSite")} *
              </Label>
              <select
                id="site"
                value={formData.site_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    site_id: e.target.value,
                    category_id: undefined,
                  }))
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                required
                disabled={sitesLoading}
              >
                <option value="">
                  {t("articleGeneration.topic.selectSite")}
                </option>
                {sites.map((site: Site) => (
                  <option key={site.id} value={site.id}>
                    {site.name} ({site.url})
                  </option>
                ))}
              </select>
              {sites.length === 0 && !sitesLoading && (
                <p className="text-sm text-destructive">
                  {t("articleGeneration.topic.noActiveSites")}
                </p>
              )}
            </div>

            {/* Category Selection */}
            {formData.site_id && (
              <div className="space-y-2">
                <Label htmlFor="category">
                  {t("articleGeneration.topic.category", "Category")}
                </Label>
                <select
                  id="category"
                  value={formData.category_id || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      category_id: e.target.value
                        ? parseInt(e.target.value, 10)
                        : undefined,
                    }))
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={categoriesLoading}
                >
                  <option value="">
                    {t(
                      "articleGeneration.topic.selectCategory",
                      "Select category (optional)",
                    )}
                  </option>
                  {categories.map((cat: WordPressCategory) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {categoriesLoading && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t(
                      "articleGeneration.topic.loadingCategories",
                      "Loading categories...",
                    )}
                  </p>
                )}
              </div>
            )}

            {/* Topic */}
            <div className="space-y-2">
              <Label htmlFor="topic">
                {t("articleGeneration.topic.articleTopic")} *
              </Label>
              <Textarea
                id="topic"
                placeholder={t("articleGeneration.topic.topicPlaceholder")}
                value={formData.topic}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, topic: e.target.value }))
                }
                rows={3}
                required
              />
              <p className="text-sm text-muted-foreground">
                {t("articleGeneration.topic.topicDescription")}
              </p>
            </div>

            {/* Content Requirements - PROMINENT (MOVED UP - THIS IS THE MOST IMPORTANT FIELD) */}
            <div className="space-y-2 p-4 rounded-lg border-2 border-primary/30 bg-primary/5">
              <Label
                htmlFor="contentRequirements"
                className="flex items-center gap-2 text-base font-semibold"
              >
                <FileText className="h-4 w-4 text-primary" />
                {t("articleGeneration.topic.contentRequirements")}
              </Label>
              <Textarea
                id="contentRequirements"
                placeholder={t(
                  "articleGeneration.topic.contentRequirementsPlaceholder",
                )}
                value={formData.additional_instructions || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    additional_instructions: e.target.value,
                  }))
                }
                rows={5}
                className="bg-background"
              />
              <p className="text-sm text-muted-foreground">
                {t("articleGeneration.topic.contentRequirementsDescription")}
              </p>
              {/* Example hint */}
              <details className="text-sm">
                <summary className="cursor-pointer text-primary hover:underline">
                  {t("articleGeneration.topic.showExample")}
                </summary>
                <div className="mt-2 p-3 bg-secondary/50 rounded-md text-muted-foreground whitespace-pre-line">
                  {t("articleGeneration.topic.contentRequirementsExample")}
                </div>
              </details>
            </div>

            {/* Keywords - Optional, collapsed by default */}
            <details className="space-y-2 p-3 rounded-lg border border-secondary/50 bg-secondary/10">
              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                {t("articleGeneration.topic.advancedSeoOptional")}
              </summary>
              <div className="pt-3 space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="keywords" className="text-sm">
                    {t("articleGeneration.topic.targetKeywords")}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t("articleGeneration.topic.keywordsOptionalNote")}
                  </p>
                  <div className="flex gap-2">
                    <Input
                      id="keywords"
                      placeholder={t(
                        "articleGeneration.topic.addKeywordPlaceholder",
                      )}
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddKeyword();
                        }
                      }}
                      className="text-sm"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddKeyword()}
                    >
                      {t("app.add")}
                    </Button>
                  </div>

                  {/* Current Keywords */}
                  {formData.keywords && formData.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.keywords.map((keyword) => (
                        <span
                          key={keyword}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs"
                        >
                          {keyword}
                          <button
                            type="button"
                            onClick={() => handleRemoveKeyword(keyword)}
                            className="hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Suggested Keywords */}
                  <div className="mt-2">
                    <p className="text-xs text-muted-foreground mb-1">
                      {t("articleGeneration.topic.suggestedKeywords")}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {suggestedKeywords
                        .filter(
                          (k) => !formData.keywords?.includes(k.toLowerCase()),
                        )
                        .map((keyword) => (
                          <button
                            key={keyword}
                            type="button"
                            onClick={() => handleAddKeyword(keyword)}
                            className="px-2 py-0.5 text-xs bg-secondary hover:bg-secondary/80 rounded-md transition-colors"
                          >
                            + {keyword}
                          </button>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </details>
          </div>
        )}

        {/* Step 2: Style & Settings */}
        {currentStep === "style" && (
          <div className="space-y-6">
            {/* Target Audience */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                {t("articleGeneration.style.targetAudience")}
              </Label>
              <div className="grid grid-cols-2 gap-3">
                {audienceOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        audience: option.value,
                      }))
                    }
                    className={cn(
                      "p-3 rounded-lg border text-start transition-colors",
                      formData.audience === option.value
                        ? "border-primary bg-primary/5"
                        : "border-input hover:border-primary/50",
                    )}
                  >
                    <div className="font-medium text-sm">
                      {t(option.labelKey)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {t(option.descKey)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Tone */}
            <div className="space-y-3">
              <Label htmlFor="tone">
                {t("articleGeneration.style.writingTone")}
              </Label>
              <div className="grid grid-cols-2 gap-3">
                {(
                  ["professional", "casual", "formal", "friendly"] as const
                ).map((tone) => (
                  <button
                    key={tone}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, tone }))}
                    className={cn(
                      "p-3 rounded-lg border text-start transition-colors",
                      formData.tone === tone
                        ? "border-primary bg-primary/5"
                        : "border-input hover:border-primary/50",
                    )}
                  >
                    <div className="font-medium text-sm">
                      {t(toneKeys[tone]?.labelKey || "")}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {t(toneKeys[tone]?.descKey || "")}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Language */}
            <div className="space-y-2">
              <Label htmlFor="language">
                {t("articleGeneration.style.language")}
              </Label>
              <select
                id="language"
                value={formData.language}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    language: e.target.value as "en" | "he",
                  }))
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="en">
                  {t("articleGeneration.style.english")}
                </option>
                <option value="he">
                  {t("articleGeneration.style.hebrew")}
                </option>
              </select>
            </div>

            {/* Word Count */}
            <div className="space-y-2">
              <Label htmlFor="wordCount">
                {t("articleGeneration.style.targetWordCount", {
                  count: formData.target_word_count,
                })}
              </Label>
              <input
                type="range"
                id="wordCount"
                min={500}
                max={5000}
                step={100}
                value={formData.target_word_count}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    target_word_count: parseInt(e.target.value),
                  }))
                }
                className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  {t("articleGeneration.style.words", { count: 500 })}
                </span>
                <span>
                  {t("articleGeneration.style.words", { count: 2500 })}
                </span>
                <span>
                  {t("articleGeneration.style.words", { count: 5000 })}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Advanced Options */}
        {currentStep === "advanced" && (
          <div className="space-y-6">
            {/* Image Generation */}
            <div className="p-4 rounded-lg border space-y-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="includeImage"
                  checked={formData.include_image}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      include_image: e.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <div>
                  <Label
                    htmlFor="includeImage"
                    className="cursor-pointer font-medium"
                  >
                    {t("articleGeneration.advanced.generateFeaturedImage")}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("articleGeneration.advanced.featuredImageDescription")}
                  </p>
                </div>
              </div>
              {formData.include_image && (
                <div className="space-y-2 ps-7">
                  <Label htmlFor="imageInstructions">
                    {t("articleGeneration.advanced.imageInstructions")}
                  </Label>
                  <Textarea
                    id="imageInstructions"
                    placeholder={t(
                      "articleGeneration.advanced.imageInstructionsPlaceholder",
                    )}
                    value={formData.image_instructions || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        image_instructions: e.target.value,
                      }))
                    }
                    rows={2}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "articleGeneration.advanced.imageInstructionsDescription",
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* H1 title in content */}
            <div className="p-4 rounded-lg border">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="includeH1"
                  checked={formData.include_h1 || false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      include_h1: e.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <div>
                  <Label
                    htmlFor="includeH1"
                    className="cursor-pointer font-medium"
                  >
                    Include H1 title in content
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Most WordPress themes render the post title as an H1
                    automatically. Leave unchecked to avoid a duplicate H1 tag.
                  </p>
                </div>
              </div>
            </div>

            {/* Auto-publish to WordPress */}
            <div className="p-4 rounded-lg border">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="autoPublish"
                  checked={formData.auto_publish || false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      auto_publish: e.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <div>
                  <Label
                    htmlFor="autoPublish"
                    className="cursor-pointer font-medium"
                  >
                    Auto-publish to WordPress when ready
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    When the article finishes generating, publish it directly to
                    the selected WordPress site (status = published). Leave
                    unchecked to review first.
                  </p>
                </div>
              </div>
            </div>

            {/* Reference URLs — dynamic per-link inputs */}
            <div className="p-4 rounded-lg border space-y-3">
              <Label className="font-medium">
                Reference links (optional, up to 5)
              </Label>
              <p className="text-xs text-muted-foreground">
                The agent picks the best phrase in the article body and wraps it
                as a hyperlink. YouTube links are embedded at a natural spot.
              </p>
              {(formData.reference_urls && formData.reference_urls.length > 0
                ? formData.reference_urls
                : [""]
              ).map((url, idx) => {
                const isYouTube =
                  /(?:youtube\.com\/(?:watch|shorts|embed)|youtu\.be\/)/i.test(
                    url,
                  );
                const isValid = !url || /^https?:\/\/.+/.test(url);
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Input
                        placeholder="https://example.com/article"
                        value={url}
                        onChange={(e) => {
                          const next = [...(formData.reference_urls || [])];
                          next[idx] = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            reference_urls: next,
                          }));
                        }}
                        onPaste={(e) => {
                          const pasted = e.clipboardData.getData("text");
                          if (pasted.includes("\n")) {
                            e.preventDefault();
                            const urls = pasted
                              .split(/\r?\n/)
                              .map((u) => u.trim())
                              .filter(Boolean);
                            const current = [
                              ...(formData.reference_urls || []),
                            ].filter(Boolean);
                            const merged = [...current, ...urls].slice(0, 5);
                            setFormData((prev) => ({
                              ...prev,
                              reference_urls: merged,
                            }));
                          }
                        }}
                        className={
                          !isValid
                            ? "border-red-400"
                            : url && isValid
                              ? "border-green-400"
                              : ""
                        }
                      />
                      {url && isYouTube && (
                        <span className="absolute end-10 top-1/2 -translate-y-1/2 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                          ▶ YouTube
                        </span>
                      )}
                    </div>
                    {(formData.reference_urls || []).length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const next = (formData.reference_urls || []).filter(
                            (_, i) => i !== idx,
                          );
                          setFormData((prev) => ({
                            ...prev,
                            reference_urls: next.length ? next : [""],
                          }));
                        }}
                        className="px-2 text-muted-foreground"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                );
              })}
              {(formData.reference_urls || []).length < 5 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const next = [...(formData.reference_urls || []), ""];
                    setFormData((prev) => ({ ...prev, reference_urls: next }));
                  }}
                >
                  + Add another link
                </Button>
              )}
            </div>

            {/* SEO/GEO Focus */}
            <div className="p-4 rounded-lg bg-secondary/30">
              <div className="flex items-center gap-2 mb-3">
                <Target className="h-4 w-4 text-primary" />
                <span className="font-medium">
                  {t("articleGeneration.advanced.optimizationTargets")}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">
                    {t("articleGeneration.advanced.seoScoreTarget")}
                  </div>
                  <div className="font-medium">80+</div>
                </div>
                <div>
                  <div className="text-muted-foreground">
                    {t("articleGeneration.advanced.geoScoreTarget")}
                  </div>
                  <div className="font-medium">80+</div>
                </div>
                <div>
                  <div className="text-muted-foreground">
                    {t("articleGeneration.advanced.humanScoreTarget")}
                  </div>
                  <div className="font-medium">85+</div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                {t("articleGeneration.advanced.targetsDescription")}
              </p>
            </div>
          </div>
        )}

        {/* Step 4: Preview & Generate */}
        {currentStep === "preview" && (
          <div className="space-y-6">
            <div className="rounded-lg border p-4 space-y-4">
              <h3 className="font-medium flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t("articleGeneration.preview.generationSummary")}
              </h3>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">
                    {t("articleGeneration.preview.targetSite")}
                  </div>
                  <div className="font-medium">
                    {selectedSite?.name ||
                      t("articleGeneration.preview.notSelected")}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">
                    {t("articleGeneration.preview.language")}
                  </div>
                  <div className="font-medium">
                    {formData.language === "en"
                      ? t("articleGeneration.style.english")
                      : t("articleGeneration.style.hebrew")}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">
                    {t("articleGeneration.preview.tone")}
                  </div>
                  <div className="font-medium">
                    {t(
                      toneKeys[formData.tone || "professional"]?.labelKey || "",
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">
                    {t("articleGeneration.preview.targetLength")}
                  </div>
                  <div className="font-medium">
                    {t("articleGeneration.style.words", {
                      count: formData.target_word_count,
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">
                    {t("articleGeneration.preview.targetAudience")}
                  </div>
                  <div className="font-medium">
                    {formData.audience
                      ? t(
                          audienceOptions.find(
                            (a) => a.value === formData.audience,
                          )?.labelKey || "",
                        )
                      : ""}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">
                    {t("articleGeneration.preview.category", "Category")}
                  </div>
                  <div className="font-medium">
                    {formData.category_id
                      ? categories.find((c) => c.id === formData.category_id)
                          ?.name || t("articleGeneration.preview.notSelected")
                      : t(
                          "articleGeneration.preview.defaultCategory",
                          "Default",
                        )}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">
                    {t("articleGeneration.preview.featuredImage")}
                  </div>
                  <div className="font-medium">
                    {formData.include_image
                      ? t("articleGeneration.preview.yesWithDalle")
                      : t("app.no")}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-muted-foreground text-sm">
                  {t("articleGeneration.preview.topic")}
                </div>
                <div className="font-medium mt-1 p-2 bg-secondary/50 rounded">
                  {formData.topic}
                </div>
              </div>

              {formData.keywords && formData.keywords.length > 0 && (
                <div>
                  <div className="text-muted-foreground text-sm mb-2">
                    {t("articleGeneration.preview.keywords")}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.keywords.map((keyword) => (
                      <span
                        key={keyword}
                        className="px-2 py-1 bg-primary/10 text-primary rounded-full text-sm"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {formData.additional_instructions && (
                <div>
                  <div className="text-muted-foreground text-sm">
                    {t("articleGeneration.preview.additionalInstructions")}
                  </div>
                  <div className="mt-1 p-2 bg-secondary/50 rounded text-sm">
                    {formData.additional_instructions}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-start gap-3">
                <Sparkles className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <div className="font-medium">
                    {t("articleGeneration.preview.readyToGenerate")}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("articleGeneration.preview.readyDescription")}
                  </p>
                </div>
              </div>
            </div>

            {/* Celery Unavailable Warning */}
            {!healthLoading && !celeryAvailable && (
              <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                  <div>
                    <div className="font-medium text-destructive">
                      {t("articleGeneration.preview.systemUnavailable")}
                    </div>
                    <p className="text-sm text-destructive/80 mt-1">
                      {t(
                        "articleGeneration.preview.systemUnavailableDescription",
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {generateMutation.error && (
              <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                {(generateMutation.error as Error).message ||
                  t("articleGeneration.preview.failedToStart")}
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-6 mt-6 border-t">
          <div>
            {onCancel && currentStepIndex === 0 && (
              <Button type="button" variant="outline" onClick={onCancel}>
                {t("app.cancel")}
              </Button>
            )}
            {currentStepIndex > 0 && (
              <Button type="button" variant="outline" onClick={prevStep}>
                <ChevronLeft className="me-1 h-4 w-4" />
                {t("app.back")}
              </Button>
            )}
          </div>

          <div>
            {currentStepIndex < steps.length - 1 ? (
              <Button onClick={nextStep} disabled={!canProceed()}>
                {t("app.next")}
                <ChevronRight className="ms-1 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={
                  !formData.site_id ||
                  !formData.topic ||
                  generateMutation.isPending ||
                  !celeryAvailable
                }
              >
                {generateMutation.isPending ? (
                  <>
                    <Loader2 className="me-2 h-4 w-4 animate-spin" />
                    {t("articleGeneration.actions.startingGeneration")}
                  </>
                ) : (
                  <>
                    <Sparkles className="me-2 h-4 w-4" />
                    {t("articleGeneration.actions.generateArticle")}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
